(function () {
  'use strict';

  const WIDGET_SELECTOR = '[data-cart-addon]';
  const LOG_PREFIX = '[CartAddon]';

  function findWidgets(root = document) {
    return Array.from(root.querySelectorAll(WIDGET_SELECTOR));
  }

  function getAddonVariantIds() {
    return findWidgets()
      .map((el) => el.dataset.variantId)
      .filter(Boolean);
  }

  function getWidgetConfig(widget) {
    if (!widget) return null;

    const variantId = widget.dataset.variantId;
    const lineProperty = widget.dataset.lineProperty;
    if (!variantId || !lineProperty) return null;

    return {
      variantId: variantId,
      lineKey: widget.dataset.lineKey || '',
      lineProperty: lineProperty,
    };
  }

  async function fetchJSON(url, options = {}) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(LOG_PREFIX + ' Request failed:', error);
      throw error;
    }
  }

  async function getCart() {
    try {
      return await fetchJSON('/cart.js');
    } catch (error) {
      console.error(LOG_PREFIX + ' Failed to fetch cart:', error);
      return null;
    }
  }

  function getSectionsToRender() {
    const miniCart = document.querySelector('mini-cart');
    const cartItems = document.querySelector('cart-items');

    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return cartItems.getSectionsToRender().map((section) => section.section);
    }

    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      return miniCart.getSectionsToRender().map((section) => section.id);
    }

    return [];
  }

  function updateCartPageSections(responseData) {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || !responseData.sections) return;

    if (typeof cartItems.getSectionsToRender === 'function' && typeof cartItems.getSectionInnerHTML === 'function') {
      cartItems.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const elementToReplace = element.querySelector(section.selector) || element;
          if (elementToReplace && responseData.sections[section.section]) {
            elementToReplace.innerHTML = cartItems.getSectionInnerHTML(
              responseData.sections[section.section],
              section.selector
            );
          }
        }
      });
    }
  }

  function refreshMiniCart(responseData) {
    const miniCart = document.querySelector('mini-cart');
    if (!miniCart || !miniCart.renderContents || !responseData.sections) return;

    const sectionsToRender = miniCart.getSectionsToRender();
    const allSectionsExist = sectionsToRender.every((section) => responseData.sections[section.id]);

    if (allSectionsExist) {
      miniCart.renderContents(responseData);
    } else {
      console.warn(LOG_PREFIX + ' Some sections missing from response', {
        requested: sectionsToRender.map((s) => s.id),
        received: Object.keys(responseData.sections || {}),
      });
    }
  }

  function dispatchCartUpdated(responseData) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: responseData },
      })
    );
  }

  async function addVariantToCart(variantId, lineProperty) {
    try {
      const sections = getSectionsToRender();
      const properties = {};
      properties[lineProperty] = 'true';

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sections,
          sections_url: window.location.pathname,
          properties: properties,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add variant');
      }

      const responseData = await response.json();
      updateCartPageSections(responseData);
      refreshMiniCart(responseData);

      setTimeout(() => initAllWidgets(), 50);
      dispatchCartUpdated(responseData);
      return true;
    } catch (error) {
      console.error(LOG_PREFIX + ' Failed to add variant:', error);
      return false;
    }
  }

  async function removeLineFromCart(lineKey) {
    try {
      const cartItems = document.querySelector('cart-items');
      const sections = cartItems
        ? cartItems.getSectionsToRender().map((section) => section.section)
        : getSectionsToRender();

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: { [lineKey]: 0 },
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove line');
      }

      const responseData = await response.json();
      updateCartPageSections(responseData);
      refreshMiniCart(responseData);

      setTimeout(() => initAllWidgets(), 50);
      dispatchCartUpdated(responseData);
      return true;
    } catch (error) {
      console.error(LOG_PREFIX + ' Failed to remove line:', error);
      return false;
    }
  }

  function isVariantInCart(cart, variantId) {
    if (!cart || !cart.items || !variantId) return false;
    return cart.items.some((item) => String(item.variant_id) === String(variantId));
  }

  function getLineItem(cart, variantId) {
    if (!cart || !cart.items || !variantId) return null;
    return cart.items.find((item) => String(item.variant_id) === String(variantId)) || null;
  }

  async function checkAndRemoveAddonsIfCartEmpty() {
    const widgets = findWidgets();
    if (widgets.length === 0) return;

    const cart = await getCart();
    if (!cart || !cart.items || cart.items.length === 0) return;

    const addonIds = getAddonVariantIds();
    const nonAddonItems = cart.items.filter((item) => {
      return !addonIds.some((id) => String(item.variant_id) === String(id));
    });

    if (nonAddonItems.length > 0) return;

    const updates = {};
    cart.items.forEach((item) => {
      if (addonIds.some((id) => String(item.variant_id) === String(id)) && item.key) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) return;

    try {
      const cartItems = document.querySelector('cart-items');
      const sections = cartItems
        ? cartItems.getSectionsToRender().map((section) => section.section)
        : getSectionsToRender();

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return;

      const responseData = await response.json();
      updateCartPageSections(responseData);
      refreshMiniCart(responseData);
      setTimeout(() => initAllWidgets(), 50);
      dispatchCartUpdated(responseData);
    } catch (error) {
      console.error(LOG_PREFIX + ' Failed to clear addon items:', error);
    }
  }

  async function updateWidgetLineKey(widget) {
    const config = getWidgetConfig(widget);
    if (!config) return;

    const cart = await getCart();
    if (!cart) return;

    const lineItem = getLineItem(cart, config.variantId);
    if (lineItem && lineItem.key) {
      widget.dataset.lineKey = lineItem.key;
    }
  }

  async function handleToggle(widget, checkbox) {
    const config = getWidgetConfig(widget);
    if (!config) return;

    const loadingOverlay = widget.querySelector('.sp-widget__loading');

    checkbox.disabled = true;
    widget.classList.add('loading');
    if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    const wasChecked = checkbox.checked;

    try {
      const cart = await getCart();
      if (!cart) {
        checkbox.checked = !wasChecked;
        return;
      }

      const isCurrentlyInCart = isVariantInCart(cart, config.variantId);
      const lineItem = getLineItem(cart, config.variantId);
      let success = false;

      if (wasChecked && !isCurrentlyInCart) {
        success = await addVariantToCart(config.variantId, config.lineProperty);
      } else if (!wasChecked && isCurrentlyInCart && lineItem) {
        success = await removeLineFromCart(lineItem.key || config.lineKey);
      } else {
        success = true;
      }

      if (success) {
        await updateWidgetLineKey(widget);
      } else {
        checkbox.checked = !wasChecked;
      }
    } catch (error) {
      console.error(LOG_PREFIX + ' Toggle failed:', error);
      checkbox.checked = !wasChecked;
    } finally {
      checkbox.disabled = false;
      widget.classList.remove('loading');
      if (loadingOverlay) loadingOverlay.classList.add('hidden');
    }
  }

  function initWidget(widget) {
    const checkbox = widget.querySelector('.sp-switch input[type="checkbox"]');
    if (!checkbox) return;

    const newCheckbox = checkbox.cloneNode(true);
    checkbox.parentNode.replaceChild(newCheckbox, checkbox);

    newCheckbox.addEventListener('change', () => {
      handleToggle(widget, newCheckbox);
    });
  }

  function initAllWidgets() {
    findWidgets().forEach((widget) => {
      initWidget(widget);
      updateWidgetLineKey(widget);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllWidgets);
  } else {
    initAllWidgets();
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches && node.matches(WIDGET_SELECTOR)) {
          initWidget(node);
          updateWidgetLineKey(node);
        }

        findWidgets(node).forEach((widget) => {
          initWidget(widget);
          updateWidgetLineKey(widget);
        });
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('cart:updated', () => {
    setTimeout(() => {
      checkAndRemoveAddonsIfCartEmpty();
    }, 200);
  });

  window.CartAddonWidget = {
    refresh: initAllWidgets,
  };

  // Back-compat aliases
  window.ShippingProtectionWidget = window.CartAddonWidget;
  window.NbcfDonationWidget = window.CartAddonWidget;
})();
